// src/services/apiClient.ts
import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import authService from './authService';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to attach auth token
apiClient.interceptors.request.use(
  (config) => {
    const tokens = authService.getTokens();
    
    if (tokens?.accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    // If error is 401 Unauthorized and we have an access token
    if (error.response?.status === 401 && !originalRequest?.headers?.['X-Retry']) {
      try {
        // Try to refresh the token
        await authService.refreshToken();
        
        // Clone the original request
        const newRequest = { ...originalRequest };
        
        // Mark as retried to prevent infinite loop
        if (newRequest.headers) {
          newRequest.headers['X-Retry'] = 'true';
          
          // Update with new token
          const tokens = authService.getTokens();
          if (tokens?.accessToken) {
            newRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          }
        }
        
        // Retry the original request with new token
        return apiClient(newRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and redirect to login
        authService.logout();
        
        // Use a cleaner way to redirect to login
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    // If 404 Not Found
    if (error.response?.status === 404) {
      console.warn(`Route not found: ${originalRequest?.url}`);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;